import type { Observable } from 'rxjs';
import type { Uuid } from '@common/types';
import type { SitePageCopyRequest, SitePageEntity } from '@common/types/site-page.type';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import * as PagesSelectors from './pages.selectors';
import { selectIsPersonalSite } from './pages.selectors';
import PagesActions from './pages.actions';

@Injectable()
export class PagesFacade {
    private readonly store: Store = inject(Store);

    public readonly pages$: Observable<SitePageEntity[]> = this.store.select(PagesSelectors.selectPages);
    public readonly isEduSite$: Observable<boolean> = this.store.select(PagesSelectors.selectHasEduPages);
    public readonly isPublishing$: Observable<boolean> = this.store.select(PagesSelectors.selectIsPublishing);
    public readonly selectTotal$: Observable<number> = this.store.select(PagesSelectors.selectTotal);
    public readonly currentPage$: Observable<SitePageEntity> = this.store.select(PagesSelectors.selectCurrentPage);
    public readonly isPersonalSite$: Observable<boolean> = this.store.select(selectIsPersonalSite);

    public navigateSitePage(siteId: string, pageId: string): void {
        this.store.dispatch(PagesActions.navigateSitePage({ siteId, pageId }));
    }

    public navigateSitePageFromDrawer(siteId: string, pageId: string): void {
        this.store.dispatch(PagesActions.navigateSitePageFromDrawer({ siteId, pageId }));
    }

    public addNewPage(): void {
        this.store.dispatch(PagesActions.addNewPage());
    }

    public createNewPage(page: SitePageEntity): void {
        this.store.dispatch(PagesActions.createNewPageSuccess({ page }));
    }

    public patchPage(pageId: Uuid, data: Partial<SitePageEntity>): void {
        this.store.dispatch(PagesActions.patchPage({ pageId, data }));
    }

    public activatePage(siteId: number, pageId: number): void {
        this.store.dispatch(PagesActions.activatePageRequest({ siteId, pageId }));
    }

    public disablePage(siteId: number, pageId: number): void {
        this.store.dispatch(PagesActions.disablePageRequest({ siteId, pageId }));
    }

    public deletePage(siteId: number, pageId: number): void {
        this.store.dispatch(PagesActions.deletePageRequest({ siteId, pageId }));
    }

    public copyPageHandler(siteId: number, page: SitePageEntity): void {
        this.store.dispatch(PagesActions.copyPage({ siteId, page }));
    }

    public copyPage(siteId: number, pageId: number, copyRequest: SitePageCopyRequest): void {
        this.store.dispatch(PagesActions.copyPageRequest({ siteId, pageId, copyRequest }));
    }

    public deletePageSuccess(pageId: number | string): void {
        this.store.dispatch(PagesActions.deletePageSuccess({ pageId }));
    }

    public loadMorePages(page: number, perPage: number): void {
        this.store.dispatch(PagesActions.loadMorePages({ page, perPage }));
    }
}
